import { Alert, Container, Header, SpaceBetween } from '@amzn/awsui-components-react';
import { AccessibleSuspenseLoading } from '../accessibility/accessibleSuspenseLoading';
import { UrlControlledPagination } from '../../hooks/urlControlledPagination';
import { useTranslation } from 'react-i18next';
import { DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE } from './coordinatorEventShiftsTablePaginationPreference';
import { useUrlPage } from '../../hooks/attendance/urlPage';
import { useInjectEventDetailsForFirstPageOfQueryData } from '../../hooks/events';
import { useSuspensePaginatedRedVelvetQuery } from '../../hooks/paginatedRedVelvetQuery';
import { COORDINATOR_EVENT_LIST } from '../../utils/constants';
import { EventListItem } from './components/EventListItem';
import {
  EventStatus,
  GetEventDetailsManagedByUserCommandOutput,
  GetUnpublishedEventsManagedByUserCommandOutput,
  Event,
  EventDetails,
} from '@amzn/red-velvet-api';
import { NoActivitiesViewComponent } from './components/NoActivitiesViewComponent';
import { DEFAULT_EVENT_PAGE_SIZE } from './constants';
import { useDeleteEvent } from '../../hooks/events/deleteEvent';
import { useRef, useState } from 'react';
import { CancelDraftModal } from '../events/createEvent/components/cancelDraftModal';

export type CoordinatorEventListProps = {
  startDate?: Date;
  endDate?: Date;
  sort: 'newest-first' | 'oldest-first';
  status?: EventStatus;
};

const isDraftStatus = (status?: EventStatus) =>
  status === EventStatus.DRAFT || status === EventStatus.UNPUBLISHED;

export function SuspenseCoordinatorEventList({
  startDate,
  endDate,
  sort,
  status,
}: CoordinatorEventListProps) {
  const { t } = useTranslation(['activity', 'translation']);
  const { page } = useUrlPage(1);
  const { mutate: deleteEvent } = useDeleteEvent();
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const currentEventIdInFocus = useRef('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const injectEventDetailsInReactCache = useInjectEventDetailsForFirstPageOfQueryData();
  const useUnpublishedApi = isDraftStatus(status);
  const { queryResult, refresh } = useSuspensePaginatedRedVelvetQuery(
    {
      redVelvetQueryKey: useUnpublishedApi
        ? [
            'getUnpublishedEventsManagedByUser',
            {
              descending: sort === 'newest-first',
              pageSize: DEFAULT_EVENT_PAGE_SIZE,
            },
          ]
        : [
            'getEventDetailsManagedByUser',
            {
              descending: sort === 'newest-first',
              startDateTime: startDate ? `${startDate.toISOString()}` : undefined,
              endDateTime: endDate ? `${endDate.toISOString()}` : undefined,
              shiftPageSize: DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE,
              pageSize: DEFAULT_EVENT_PAGE_SIZE,
              status: status,
            },
          ],
    },
    page
  );

  if (queryResult.error) {
    return (
      <Alert
        type="error"
        i18nStrings={{
          errorIconAriaLabel: t('coordinatorEventShiftsList.errorAria'),
          dismissAriaLabel: t('alert.dismiss.error'),
        }}
      >{`${queryResult.error}`}</Alert>
    );
  }

  if (!queryResult.data) {
    return (
      <Alert
        type="error"
        i18nStrings={{
          errorIconAriaLabel: t('coordinatorEventShiftsList.errorAria'),
          dismissAriaLabel: t('alert.dismiss.error'),
        }}
      >
        {t('coordinatorEventShiftsList.beyondPageIndex', { page })}
      </Alert>
    );
  }

  // Normalize both API response shapes into a common events array
  let events: Event[];
  if (useUnpublishedApi) {
    const unpublishedData = queryResult.data as GetUnpublishedEventsManagedByUserCommandOutput;
    events = unpublishedData?.events ?? [];
  } else {
    const data = queryResult.data as GetEventDetailsManagedByUserCommandOutput;
    const eventDetails: EventDetails[] = data?.eventDetails ?? [];
    injectEventDetailsInReactCache(eventDetails);
    events = eventDetails.map((ed) => ed.event);
  }

  if (events.length === 0) {
    return <NoActivitiesViewComponent analyticsPrefix={COORDINATOR_EVENT_LIST} />;
  }

  return (
    <SpaceBetween size="s">
      <Container>
        <ul style={{ padding: 0 }}>
          {events.map((event, index) => (
            <li key={event.eventId} style={{ listStyle: 'none' }}>
              {index > 0 && <hr data-volunteer-center-hr style={{ margin: 0 }} />}
              <EventListItem
                event={event}
                onDelete={() => {
                  currentEventIdInFocus.current = event.eventId;
                  setIsCancelModalVisible(true);
                }}
              />
            </li>
          ))}
        </ul>
        <CancelDraftModal
          visible={isCancelModalVisible}
          loading={isLoading}
          variant="list"
          onConfirm={() => {
            setIsLoading(true);
            deleteEvent(
              { eventId: currentEventIdInFocus.current },
              {
                onSuccess: () => {
                  setIsLoading(false);
                  refresh();
                },
                onError: () => {
                  setIsLoading(false);
                  setHasError(true);
                  setIsCancelModalVisible(false);
                },
              }
            );
          }}
          onDismiss={() => setIsCancelModalVisible(false)}
        />
      </Container>
      {hasError && (
        <Alert
          type="error"
          header={t('deleteDraftErrorHeader')}
          dismissible
          onDismiss={() => setHasError(false)}
        >
          {t('deleteDraftError')}
        </Alert>
      )}
    </SpaceBetween>
  );
}

export function CoordinatorEventList({
  startDate,
  endDate,
  sort,
  status,
}: CoordinatorEventListProps) {
  const namespaces = ['activity', 'translation'];
  const { t } = useTranslation(namespaces);
  const getEventDetailsManagedByUserAPIInput = {
    pageSize: DEFAULT_EVENT_PAGE_SIZE,
    descending: sort === 'newest-first',
    startDateTime: startDate ? `${startDate.toISOString()}` : undefined,
    endDateTime: endDate ? `${endDate.toISOString()}` : undefined,
    shiftPageSize: DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE,
    status: status,
  };
  const getUnpublishedEventsManagedByUserAPIInput = {
    descending: sort === 'newest-first',
    pageSize: DEFAULT_EVENT_PAGE_SIZE,
  };
  const useUnpublishedApi = isDraftStatus(status);
  const paginationProps = {
    paginationPrefix: 'coordinatorEventShiftsList.pagination',
    namespaces: namespaces,
    tableName: t('coordinatorEventShiftsList.tableName'),
  };
  return (
    <SpaceBetween direction="vertical" size="s">
      <Header
        actions={
          <UrlControlledPagination
            redVelvetQueryKey={
              useUnpublishedApi
                ? ['getUnpublishedEventsManagedByUser', getUnpublishedEventsManagedByUserAPIInput]
                : ['getEventDetailsManagedByUser', getEventDetailsManagedByUserAPIInput]
            }
            {...paginationProps}
          />
        }
      />
      <AccessibleSuspenseLoading loadingText={t('coordinatorEventShiftsList.loading')}>
        <SuspenseCoordinatorEventList
          startDate={startDate}
          endDate={endDate}
          sort={sort}
          status={status}
        />
      </AccessibleSuspenseLoading>
      <Header
        actions={
          <UrlControlledPagination
            redVelvetQueryKey={
              useUnpublishedApi
                ? ['getUnpublishedEventsManagedByUser', getUnpublishedEventsManagedByUserAPIInput]
                : ['getEventDetailsManagedByUser', getEventDetailsManagedByUserAPIInput]
            }
            {...paginationProps}
          />
        }
      />
    </SpaceBetween>
  );
}
